import React, { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';

const SupabaseDiagnostic: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [connection, setConnection] = useState<'success' | 'error' | 'pending'>('pending');
  const [tables, setTables] = useState<string[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [logs, setLogs] = useState<string[]>([]);

  const addLog = (message: string) => {
    setLogs(prev => [...prev, `[${new Date().toISOString()}] ${message}`]);
  };

  useEffect(() => {
    const checkConnection = async () => {
      try {
        addLog('Checking Supabase connection...');
        
        // Check that we can connect to Supabase
        const { data, error } = await supabase.from('user_profiles').select('count(*)', { count: 'exact' });
        
        if (error) {
          addLog(`Connection error: ${error.message}`);
          setConnection('error');
          setError(error.message);
          return;
        }
        
        addLog('Connected to Supabase successfully');
        setConnection('success');
        
        // Get list of tables
        try {
          addLog('Fetching available tables...');
          const { data: tablesData, error: tablesError } = await supabase.rpc('get_tables');
          
          if (tablesError) {
            addLog(`Error fetching tables: ${tablesError.message}`);
            setError(prev => `${prev || ''}\nTables error: ${tablesError.message}`);
          } else if (tablesData) {
            setTables(tablesData);
            addLog(`Found ${tablesData.length} tables: ${tablesData.join(', ')}`);
          }
        } catch (e) {
          addLog(`Exception in table check: ${e instanceof Error ? e.message : String(e)}`);
        }
        
        // Check user_profiles table specifically
        try {
          addLog('Checking user_profiles table schema...');
          const { error: schemaError } = await supabase
            .from('user_profiles')
            .select('id')
            .limit(1);
            
          if (schemaError) {
            addLog(`Error with user_profiles table: ${schemaError.message}`);
            setError(prev => `${prev || ''}\nuser_profiles error: ${schemaError.message}`);
          } else {
            addLog('user_profiles table exists and is accessible');
          }
        } catch (e) {
          addLog(`Exception checking user_profiles: ${e instanceof Error ? e.message : String(e)}`);
        }
      } catch (e) {
        addLog(`Unhandled exception: ${e instanceof Error ? e.message : String(e)}`);
        setConnection('error');
        setError(e instanceof Error ? e.message : String(e));
      } finally {
        setLoading(false);
      }
    };
    
    checkConnection();
  }, []);

  return (
    <div style={{
      padding: '20px',
      maxWidth: '800px',
      margin: '0 auto',
      backgroundColor: '#1a1a1a',
      color: '#f5f5f5',
      fontFamily: 'monospace',
      borderRadius: '8px',
      marginTop: '20px'
    }}>
      <h2>Supabase Diagnostic</h2>
      
      <div style={{ marginBottom: '20px' }}>
        <h3>Connection Status</h3>
        <div style={{
          padding: '10px',
          backgroundColor: connection === 'success' ? '#143d19' : connection === 'error' ? '#3d1414' : '#1e293b',
          borderRadius: '4px',
          marginBottom: '10px'
        }}>
          {connection === 'pending' && 'Testing connection...'}
          {connection === 'success' && '✅ Connected to Supabase'}
          {connection === 'error' && '❌ Connection failed'}
        </div>
        
        {error && (
          <div style={{ 
            padding: '10px', 
            backgroundColor: '#3d1414', 
            color: '#f87171',
            borderRadius: '4px',
            whiteSpace: 'pre-wrap'
          }}>
            {error}
          </div>
        )}
      </div>
      
      <div style={{ marginBottom: '20px' }}>
        <h3>Tables ({tables.length})</h3>
        <ul style={{ 
          listStyle: 'none', 
          padding: '10px',
          backgroundColor: '#2a2a2a', 
          borderRadius: '4px',
          margin: 0
        }}>
          {tables.length === 0 && <li>No tables found</li>}
          {tables.map(table => (
            <li key={table} style={{ marginBottom: '5px' }}>
              • {table}
            </li>
          ))}
        </ul>
      </div>
      
      <div>
        <h3>Logs</h3>
        <div style={{ 
          backgroundColor: '#2a2a2a', 
          padding: '10px',
          borderRadius: '4px',
          maxHeight: '300px',
          overflowY: 'auto',
          fontSize: '12px'
        }}>
          {logs.map((log, index) => (
            <div key={index} style={{ marginBottom: '5px' }}>{log}</div>
          ))}
          {logs.length === 0 && <div>No logs yet</div>}
        </div>
      </div>
      
      <button 
        onClick={() => window.location.href = '/'}
        style={{
          marginTop: '20px',
          padding: '8px 16px',
          backgroundColor: '#3b82f6',
          color: 'white',
          border: 'none',
          borderRadius: '4px',
          cursor: 'pointer'
        }}
      >
        Back to App
      </button>
    </div>
  );
};

export default SupabaseDiagnostic; 